import CircularLoading from 'components/base/loading/CircularLoading';
import { getStringByte, validInputString } from 'lib/library';
import { throttle } from 'lodash';
import React, { ChangeEvent, ReactNode, useEffect, useRef, useState, useCallback } from 'react';
import { Control, useController } from 'react-hook-form';
import InfiniteScroll from 'react-infinite-scroll-component';
import styled from 'styled-components';
import CustomText from '../text/CustomText';

export interface ISearchDropdownOption {
  value: string | number;
  label: string;
  labelRightIcon?: ReactNode;
  itemIcon?: ReactNode;
}

interface SearchDropdownControllerProps {
  control: Control;
  name: string;
  label?: string;
  options: ISearchDropdownOption[]; // 드롭다운 옵션을 받는 props
  emptyComponent?: ReactNode;
  bottomComponent?: ReactNode;
  hasMoreData: boolean;
  disabled?: boolean;
  required?: boolean;
  loading?: boolean;
  maxLength?: number;
  placeholder?: string;
  onOptionClick?: (value: string | number, label: string) => void;
  onSearch: (value: string) => void;
  onFetch: any;
}

function SearchDropdownController({
  control,
  name,
  label = '',
  options,
  emptyComponent,
  bottomComponent,
  hasMoreData,
  loading = false,
  disabled = false,
  required = false,
  maxLength,
  placeholder = '',
  onOptionClick,
  onSearch,
  onFetch,
}: SearchDropdownControllerProps) {
  const {
    field,
    meta: { invalid },
  } = useController({
    name,
    control,
    rules: { required: required },
  });

  const [inputText, setInputText] = useState('');
  const [isListOpen, setIsListOpen] = useState(false);
  const [isComposing, setIsComposing] = useState(false);
  const [isUserTyping, setIsUserTyping] = useState(false);
  const searchDropdownRef = useRef<HTMLDivElement>(null);
  const throttledSearchRef = useRef<any>(null);
  const isSelectingRef = useRef(false);
  const typingTimerRef = useRef<any>(null);

  useEffect(() => {
    // 사용자가 타이핑 중이거나 한글 조합 중이 아닐 때만 label로 업데이트
    if (!isUserTyping && !isComposing && !isSelectingRef.current) {
      setInputText(label || '');
    }
  }, [label, isUserTyping, isComposing]);

  // throttle된 검색 함수
  const throttledSearch = useCallback((value: string) => {
    if (throttledSearchRef.current) {
      throttledSearchRef.current.cancel();
    }
    
    throttledSearchRef.current = throttle(() => {
      if (!isSelectingRef.current) {
        onSearch(value);
        setIsListOpen(true);
      }
    }, 500);
    
    throttledSearchRef.current();
  }, [onSearch]);

  const handleInputChange = (event?: ChangeEvent<HTMLInputElement>) => {
    if (loading) return;

    const validInputValue = validInputString(event?.target.value);
    setInputText(validInputValue);
    setIsUserTyping(true);
    
    // 타이핑 타이머 리셋 - 1초 후 자동으로 isUserTyping을 false로
    if (typingTimerRef.current) {
      clearTimeout(typingTimerRef.current);
    }
    typingTimerRef.current = setTimeout(() => {
      setIsUserTyping(false);
    }, 1000);
    
    // 한글 조합 중이 아닐 때만 검색
    if (!isComposing) {
      throttledSearch(validInputValue);
    }
  };

  const handleInputBlur = () => {
    setTimeout(() => {
      setIsListOpen(false);
      setIsUserTyping(false);
      if (typingTimerRef.current) {
        clearTimeout(typingTimerRef.current);
      }
    }, 200);
  };

  const handleInputFocus = () => {
    // if (inputText === '') {
    //   setIsListOpen(true);
    // }
  };

  const handleCompositionStart = () => {
    setIsComposing(true);
  };

  const handleCompositionEnd = (event: React.CompositionEvent<HTMLInputElement>) => {
    setIsComposing(false);
    const validInputValue = validInputString(event.currentTarget.value);
    setInputText(validInputValue);
    setIsUserTyping(true);
    
    // 타이핑 타이머 리셋
    if (typingTimerRef.current) {
      clearTimeout(typingTimerRef.current);
    }
    typingTimerRef.current = setTimeout(() => {
      setIsUserTyping(false);
    }, 1000);
    
    // 선택 중이 아닐 때만 검색
    if (!isSelectingRef.current) {
      onSearch(validInputValue);
      setIsListOpen(true);
    }
  };

  const handleOptionClick = (value: string | number) => {
    // 선택 중임을 표시
    isSelectingRef.current = true;
    
    // throttle된 검색 취소
    if (throttledSearchRef.current) {
      throttledSearchRef.current.cancel();
    }
    
    // 타이핑 타이머 클리어
    if (typingTimerRef.current) {
      clearTimeout(typingTimerRef.current);
    }
    
    const label = options.find(option => option.value === value)?.label;
    !!onOptionClick && onOptionClick(value, label || '');

    field.onChange(value);
    setInputText(label || '');
    setIsUserTyping(false);
    
    // 선택 완료 후 플래그 리셋
    setTimeout(() => {
      isSelectingRef.current = false;
    }, 700);
  };

  return (
    <StyledSearchDropdownController ref={searchDropdownRef}>
      <input
        className="searchDropdownController__input"
        type="text"
        value={inputText}
        maxLength={maxLength}
        disabled={disabled}
        onChange={handleInputChange}
        onCompositionStart={handleCompositionStart}
        onCompositionEnd={handleCompositionEnd}
        onFocus={handleInputFocus}
        onBlur={handleInputBlur}
        placeholder={placeholder}
      />

      {isListOpen && (
        <ul className="searchDropdownController__listbox">
          <InfiniteScroll
            className="infinite-scroll-box"
            dataLength={options?.length}
            next={onFetch}
            hasMore={hasMoreData}
            loader={
              <div className="infinite-scroll-loading">
                <CircularLoading />
              </div>
            }
            scrollThreshold={0.8}
            height={200}
          >
            {options.length > 0 ? (
              options.map(option => (
                <li
                  key={option.value}
                  className="list_option"
                  onClick={() => handleOptionClick(option.value)}
                >
                  <div className="option_label_box">
                    {option.label}
                    {!!option.labelRightIcon && option.labelRightIcon}
                  </div>
                  {!!option.itemIcon && option.itemIcon}
                </li>
              ))
            ) : (
              <>
                {!!emptyComponent && (
                  <div className="empty_box">
                    <CustomText fontSize={13} fontColor="#666">
                      {emptyComponent}
                    </CustomText>
                  </div>
                )}
              </>
            )}
          </InfiniteScroll>
        </ul>
      )}

      {!!bottomComponent && (
        <div className="searchDropdownController__bottom_box">{bottomComponent}</div>
      )}
      {invalid && <span style={{ color: 'red' }}>This field is required</span>}
    </StyledSearchDropdownController>
  );
}

const StyledSearchDropdownController = styled.div`
  position: relative;
  width: 100%;

  .searchDropdownController__input {
    width: 100%;
    height: 50px;
    border-radius: 2px;
    border: 1px solid #e1e1e7;
    padding: 12px;
    font-size: 20px;
    font-weight: 400;
    color: #222;
    &:disabled {
      background: #f9f9fb;
      color: #888;
    }
    &::placeholder {
      color: #aaa;
    }
  }

  .searchDropdownController__bottom_box {
    margin-top: 4px;
  }

  .searchDropdownController__listbox {
    z-index: 9999;
    position: absolute;
    width: 100%;
    background-color: #fff;
    box-shadow: 0px 4px 10px 0px rgba(0, 0, 0, 0.16);
    border-radius: 3px 3px 0px 0px;
    /* margin-top: 4px; */
    list-style: none;
    padding: 0;
    max-height: 200px;
    overflow-y: auto;

    .list_option {
      padding: 14px;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: space-between;
      .option_label_box {
        display: flex;
        align-items: center;
        column-gap: 5px;
      }
      &:hover {
        background-color: #eef9fd;
      }
    }
    .empty_box {
      padding: 10px;
      line-height: 18px;
    }
  }
`;

export default SearchDropdownController;
